import type { FieldDefinition } from "@/services/form-definition/form-definition-core.types";

import { type LayoutElement, LayoutTypes } from "../schemas/layout.schema";
import { resolveFieldDefinitionByLayoutId } from "./field-definition-map.util";

export interface LayoutFieldProgressRef {
	fieldDefinitionId: string;
	/** From layout `Field.layoutInfo.hidden`. */
	layoutHidden?: boolean;
}

/**
 * Whether a field value counts as completed for section progress.
 */
export function isFieldValueFilled(value: unknown): boolean {
	if (value == null) return false;
	if (typeof value === "string") return value.trim().length > 0;
	if (typeof value === "boolean") return value;
	if (typeof value === "number") return !Number.isNaN(value);
	if (Array.isArray(value)) return value.length > 0;
	if (typeof value === "object") {
		return Object.values(value as Record<string, unknown>).some(
			isFieldValueFilled,
		);
	}
	return true;
}

/**
 * Collects layout Field leaves under a subtree (every Group child counts; no `maxItems` cap).
 */
export function collectLayoutFieldProgressRefs(
	node: LayoutElement,
): LayoutFieldProgressRef[] {
	if (node.type === LayoutTypes.Field) {
		return [
			{
				fieldDefinitionId: node.fieldDefinitionId,
				layoutHidden: node.layoutInfo?.hidden,
			},
		];
	}
	if (node.type === LayoutTypes.Group) {
		return node.elements.flatMap(collectLayoutFieldProgressRefs);
	}
	return node.elements.flatMap(collectLayoutFieldProgressRefs);
}

/**
 * Mirrors {@link FieldRenderer} visibility: rule `shouldShow`, else `!layoutHidden`.
 */
export function isLayoutFieldVisible(
	fieldId: string,
	layoutHidden: boolean | undefined,
	fieldStates: Record<string, { shouldShow?: boolean } | undefined>,
): boolean {
	const state = fieldStates[fieldId];
	if (state?.shouldShow !== undefined) return state.shouldShow;
	return layoutHidden !== true;
}

export interface SectionCompletionCounts {
	completed: number;
	total: number;
}

/**
 * Counts filled vs visible fields in a section (hidden fields are excluded from both).
 */
export function countVisibleSectionCompletion(args: {
	progressRefs: LayoutFieldProgressRef[];
	formValues: Record<string, unknown>;
	fieldStates: Record<string, { shouldShow?: boolean } | undefined>;
	fieldDefinitionsById: ReadonlyMap<string, FieldDefinition>;
	fieldDefinitionsByName: ReadonlyMap<string, FieldDefinition>;
}): SectionCompletionCounts {
	const {
		progressRefs,
		formValues,
		fieldStates,
		fieldDefinitionsById,
		fieldDefinitionsByName,
	} = args;

	let filled = 0;
	let total = 0;

	for (const ref of progressRefs) {
		const def = resolveFieldDefinitionByLayoutId(
			ref.fieldDefinitionId,
			fieldDefinitionsById,
			fieldDefinitionsByName,
		);
		if (!def?.name) continue;
		if (!isLayoutFieldVisible(def.id, ref.layoutHidden, fieldStates)) continue;

		total += 1;
		if (isFieldValueFilled(formValues[def.name])) filled += 1;
	}

	return { completed: filled, total };
}
